import { useEffect, useMemo } from "react";
import { useAppSelector } from "../../../../../Store";
import { Graph_QL } from "../../../../../Services";
import { useLanControllers } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useLanControllers";
import { useSystem } from "../../../../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { SelectFieldOption_Values } from "../../../../../Definitions";
import { useDeviceInfo } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useDeviceInfo";
import { useTranslation } from "react-i18next";

export type OptionsWithRequiredNumberId = Pick<SelectFieldOption_Values, "label"> & {id: number};

export const useLcIoOptions = () => {
  const { t } = useTranslation();
  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);;

  const { data: lanControllerState, call: getLanControllers, getCpuOptions: getLanControllerCpuOptions} = useLanControllers(connection);
  const { getCpuOptions: getDeviceCpuOptions, data: deviceState } = useDeviceInfo(connection);
  const { data: systemState, call: getSystems} = useSystem(connection);

  const {lanControllerOptions, lanControllerCpuOptions } = useMemo(() => {
    if (lanControllerState[domain]) {

      const usedCpuIds = new Set(
        lanControllerState[domain].data.map(con => con.cpu_id)
      );
      
      const lanControllerOpt = lanControllerState[domain].data.map(({controller_id, name}) => ({id: controller_id , label: name ?? controller_id.toString()}));
      const lanControllerCpuOpt = lanControllerState[domain].cpuOptions
        ?.filter(cpuOptions => !usedCpuIds.has(cpuOptions.cpu_id))
        .map((cpuOption) => ({
          id: cpuOption.lan_controller_id, label: cpuOption.cpu_id 
          
        })) ?? []

      return {
        lanControllerOptions: [
          { id: NaN, label: t("devices.noLanController", {defaultValue: "No Lan controller"})},
          ...lanControllerOpt
        ],
        lanControllerCpuOptions: [
          { id: NaN, label: t("devices.noCpuId", { defaultValue: "No cpu id" }) },
          ...lanControllerCpuOpt
        ]
      };
    }
    return {lanControllerOptions: [] as OptionsWithRequiredNumberId[], lanControllerCpuOptions: [] as OptionsWithRequiredNumberId[]}
  },[domain, lanControllerState])

  const systemOptions: OptionsWithRequiredNumberId[] = useMemo(() => {
    const systemOpt = systemState[domain] ? systemState[domain].data.map(({system_id, name}) => ({id: system_id, label: name})) : []
    return [{ id: NaN, label: t("devices.noSystem", { defaultValue: "No system" }) }, ...systemOpt];
  },[domain, systemState])

  const deviceCpuOptions: OptionsWithRequiredNumberId[] = useMemo(() => {
    if (deviceState[domain]) {

      const usedCpuIds = new Set(
        deviceState[domain].data.map(con => con.cpu_id)
      );

      const deviceOpt = deviceState[domain].cpuOptions
        ?.filter(cpuOptions => !usedCpuIds.has(cpuOptions.cpu_id))
        .map((cpuOption) => ({ id: cpuOption.device_register_id, label: cpuOption.cpu_id })) ?? [];
      return [{id: NaN,  label: t("devices.noCpuId", { defaultValue: "No cpu id" })}, ...deviceOpt];
    }
    return []
  },[deviceState, domain])
  
  useEffect(() => {
    getDeviceCpuOptions()
    getLanControllerCpuOptions()
  },[])

  useEffect(() => {
    if (!systemState[domain]) {
      getSystems({fetchAmount: 500, offset: 0})
    }
  },[domain, getSystems, systemState])

  useEffect(() => {
    if (!lanControllerState[domain]) {
      getLanControllers({fetchAmount: 500, offset: 0})
    }
  },[domain, getLanControllers, lanControllerState])

  return {systemOptions, lanControllerOptions, lanControllerCpuOptions, deviceCpuOptions}
}